import { StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import VisuallyHidden from '@reach/visually-hidden';
import dayjs from 'dayjs';
import React, { useContext } from 'react';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import { getDateAriaLabel } from '../atomsHelpers';
import { getRelativeDateLocalization } from '../../../../helpers/localization/localizationUtils';

interface Props {
  saleFromLabelStyles?: StyledProps;
  saleStart: Date;
  saleEnd: Date;
  formattedSaleStart: Date;
  formattedSaleEnd: Date;
  format: string;
  showDateDisclaimer: Boolean;
  showSaleStart: Boolean;
  showSaleEnd: Boolean;
  showSaleStartText: Boolean;
  showSaleEndText: Boolean;
  dateDisclaimerStyles?: StyledProps;
  saleStartStyles?: StyledProps;
  saleStartLabelStyles?: StyledProps;
  saleEndStyles?: StyledProps;
  saleEndLabelStyles?: StyledProps;
  saleStartText?: string;
  saleEndText?: string;
  /**
   * Date format for the screen readers
   */
  departureDateScreenReader?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMMM, YYYY' | 'MMMM D, YYYY';
  /**
   * Label Key of Departure Info
   */
  departureText?: 'cmp-label--departure-date' | 'cmp-label--departure-date-colon';
  id: string;
}

function FareItemSale({
  saleStart,
  saleEnd,
  formattedSaleStart,
  formattedSaleEnd,
  showSaleStart = false,
  showSaleEnd = true,
  saleStartStyles,
  saleStartLabelStyles,
  showSaleStartText = false,
  saleEndStyles,
  saleEndLabelStyles,
  showSaleEndText = true,
  format = '',
  showDateDisclaimer = true,
  dateDisclaimerStyles = {},
  departureDateScreenReader = 'MMMM D, YYYY',
  saleStartText = 'cmp-label--sale-start-date',
  saleEndText = 'cmp-label--sale-end-date',
  id,
  ...props
}: StyledProps<Props>) {
  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const locale = getRelativeDateLocalization(translate, localizationData);

  return saleStart && saleEnd ? (
    <Box className={`fare-item-sale-info ${className}`} style={style} id={id}>
      {showSaleStart && (
        <Text className="sale-from-text" {...saleStartStyles}>
          {showSaleStartText && (
            <Text {...saleStartLabelStyles} floatLeft>
              {translate(saleStartText, 'On sale from')}
            </Text>
          )}
          <time dateTime={dayjs(saleStart).format(format)}>
            <Text ariaHidden="true" floatLeft marginLeft={showSaleStartText ? '1' : '0'}>
              {format && saleStart
                ? dayjs(saleStart)
                    .locale(locale as $TSFixMe)
                    .format(format)
                : formattedSaleStart}
            </Text>
            <VisuallyHidden>
              {' '}
              {getDateAriaLabel(
                dayjs(saleStart).format(),
                translate,
                departureDateScreenReader,
                localizationData
              )}
            </VisuallyHidden>
            {showDateDisclaimer && (
              <>
                <Text ariaHidden="true" {...dateDisclaimerStyles} floatLeft>
                  *
                </Text>
                <VisuallyHidden>
                  {' '}
                  {translate('cmp-date-disclaimer', 'Date subject to changes')}
                </VisuallyHidden>
              </>
            )}
          </time>
        </Text>
      )}
      <Box className="fr-clearfix" />
      {showSaleEnd && (
        <Text className="sale-until-text" {...saleEndStyles}>
          {showSaleEndText && (
            <Text {...saleEndLabelStyles} floatLeft>
              {translate(saleEndText, 'On sale until')}
            </Text>
          )}
          <time dateTime={dayjs(saleEnd).format(format)}>
            <Text ariaHidden="true" floatLeft marginLeft={showSaleEndText ? '1' : '0'}>
              {format && saleEnd
                ? dayjs(saleEnd)
                    .locale(locale as $TSFixMe)
                    .format(format)
                : formattedSaleEnd}
            </Text>
            <VisuallyHidden>
              {' '}
              {getDateAriaLabel(
                dayjs(saleEnd).format(),
                translate,
                departureDateScreenReader,
                localizationData
              )}
            </VisuallyHidden>
            {showDateDisclaimer && (
              <>
                <Text ariaHidden="true" {...dateDisclaimerStyles} floatLeft>
                  *
                </Text>
                <VisuallyHidden>
                  {' '}
                  {translate('cmp-date-disclaimer', 'Date subject to changes')}
                </VisuallyHidden>
              </>
            )}
          </time>
        </Text>
      )}
    </Box>
  ) : null;
}

FareItemSale.componentName = 'FareItemSale';

export default FareItemSale;
